import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { PILLARS } from '../lib/pillars';
import { calculateProfileScores } from '../lib/calculations';
import { runFullAnalysis } from '../lib/analysis-engine';
import {
  getKinseyDefinition,
  getTimeInterpretation,
  getDirectionInsight,
  getConfidenceText
} from '../lib/kinsey-data';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer
} from 'recharts';

const HAEVNSharedView = ({ profiles }) => {
  return (
    <div className="space-y-8" data-testid="shared-haevn">
      {profiles.map((profile, idx) => {
        const scores = calculateProfileScores(profile);
        const analysis = runFullAnalysis(profile, scores);
        const isSeeking = profile.mode === 'seeking';

        const radarData = PILLARS.map(p => ({
          pillar: p.shortName,
          importance: profile.drivers?.[p.id]?.importance || 0
        }));

        const topDrivers = PILLARS
          .filter(p => (profile.drivers?.[p.id]?.importance || 0) >= 7)
          .map(p => p.name);

        return (
          <Card key={idx} className="border-border bg-card" data-testid={`shared-haevn-profile-${idx}`}>
            <CardHeader>
              <span className="text-xs text-muted-foreground tracking-wide uppercase">{profile.label}</span>
              <CardTitle className="text-xl font-light mt-1">
                {isSeeking ? 'Seeking' : 'Current Reality'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Top Drivers */}
              {topDrivers.length > 0 && (
                <div>
                  <span className="text-xs text-muted-foreground block mb-2">What matters most</span>
                  <div className="flex flex-wrap gap-2">
                    {topDrivers.map(d => (
                      <span key={d} className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">{d}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Radar */}
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="75%">
                    <PolarGrid stroke="hsl(var(--border))" strokeOpacity={0.5} />
                    <PolarAngleAxis dataKey="pillar" tick={{ fill: 'hsl(var(--foreground))', fontSize: 11, fontWeight: 300 }} />
                    <Radar dataKey="importance" stroke="hsl(var(--primary))" strokeWidth={2} fill="hsl(var(--primary))" fillOpacity={0.15} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              {/* Intent */}
              {analysis.intentAnchor && (
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Intent</span>
                  <p className="text-sm text-foreground/80 leading-relaxed">{analysis.intentAnchor}</p>
                </div>
              )}

              {/* What This Means */}
              {analysis.whatThisTellsYou && (
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">What This Means</span>
                  <p className="text-sm text-foreground/80 leading-relaxed">{analysis.whatThisTellsYou}</p>
                </div>
              )}

              {/* HAEVN Summary */}
              {analysis.haevnSummary && (
                <div className="border-t border-border/30 pt-4 space-y-2">
                  <span className="text-xs text-muted-foreground block mb-2">Profile Summary</span>
                  <p className="text-sm text-foreground/80"><span className="text-foreground font-medium">What drives you:</span> {analysis.haevnSummary.whatDrivesYou}</p>
                  <p className="text-sm text-foreground/80"><span className="text-foreground font-medium">What to look for:</span> {analysis.haevnSummary.whatToLookFor}</p>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const KinseySharedView = ({ data }) => {
  const { answers, gender, result } = data;
  const presentDef = getKinseyDefinition(result.presentScore, gender);
  const pastDef = getKinseyDefinition(result.pastScore, gender);
  const interpretation = getTimeInterpretation(result.pastScore, result.presentScore, result.direction, gender);
  const directionInsight = getDirectionInsight(result);
  const confidenceText = getConfidenceText(result.confidence);

  return (
    <div className="space-y-6" data-testid="shared-kinsey">
      <Card className="border-border bg-card">
        <CardContent className="py-8 space-y-6">
          {/* Score */}
          <div className="text-center">
            <span className="text-xs text-muted-foreground block mb-1">Estimated Kinsey Score</span>
            <span className="text-5xl font-light text-foreground" data-testid="shared-kinsey-score">
              {result.presentScore !== null ? result.presentScore : '\u2014'}
            </span>
          </div>

          {/* Category + Definition */}
          {presentDef && (
            <div className="text-center border-t border-border/30 pt-4">
              <span className="text-sm text-primary font-medium">Category {presentDef.category}</span>
              <p className="text-base text-foreground/80 mt-1">{presentDef.definition}</p>
            </div>
          )}

          {/* Past → Present */}
          <div className="flex items-center justify-center gap-6 border-t border-border/30 pt-4">
            <div className="text-center">
              <span className="text-xs text-muted-foreground block mb-1">Past</span>
              <span className="text-2xl font-light text-muted-foreground">{result.pastScore !== null ? result.pastScore : '\u2014'}</span>
            </div>
            <span className="text-xl text-muted-foreground/40 mt-3">{'\u2192'}</span>
            <div className="text-center">
              <span className="text-xs text-primary block mb-1">Present</span>
              <span className="text-2xl font-light text-foreground">{result.presentScore !== null ? result.presentScore : '\u2014'}</span>
            </div>
          </div>

          {/* Interpretation */}
          <div className="border-t border-border/30 pt-4">
            <span className="text-xs text-muted-foreground block mb-2">What this suggests</span>
            <p className="text-sm text-foreground/80 leading-relaxed">{interpretation}</p>
          </div>

          {/* Direction */}
          {directionInsight && (
            <div>
              <span className="text-xs text-muted-foreground block mb-2">Direction</span>
              <p className="text-sm text-foreground/80 leading-relaxed">{directionInsight}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const SharedView = ({ onGoHome }) => {
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareId = params.get('share');
    if (!shareId) {
      setError('No share ID found');
      setLoading(false);
      return;
    }

    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/shares/${shareId}`)
      .then(res => {
        if (!res.ok) throw new Error('Share not found');
        return res.json();
      })
      .then(data => {
        setShareData(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <section className="min-h-screen flex items-center justify-center" data-testid="shared-loading">
        <p className="text-muted-foreground">Loading shared results...</p>
      </section>
    );
  }

  if (error || !shareData) {
    return (
      <section className="min-h-screen flex flex-col items-center justify-center gap-4" data-testid="shared-error">
        <p className="text-muted-foreground">This share link is no longer available.</p>
        <Button onClick={onGoHome} variant="outline" className="rounded-full px-6">
          Go to Self Discovery Hub
        </Button>
      </section>
    );
  }

  const toolLabel = shareData.type === 'haevn' ? 'HAEVN Sexual Satisfaction Profile' : 'Kinsey Estimator';

  return (
    <section className="min-h-screen py-24 px-6" data-testid="shared-view">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4 block">
            Shared Results
          </span>
          <h2 className="text-3xl sm:text-4xl font-light text-foreground mb-2">
            {toolLabel}
          </h2>
          <p className="text-sm text-muted-foreground">
            Someone shared their results with you.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          {shareData.type === 'haevn' && <HAEVNSharedView profiles={shareData.data.profiles} />}
          {shareData.type === 'kinsey' && <KinseySharedView data={shareData.data} />}
        </motion.div>

        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-sm text-muted-foreground mb-4">Want to discover your own pattern?</p>
          <Button
            onClick={onGoHome}
            className="rounded-full px-8"
            data-testid="shared-try-btn"
          >
            Try it yourself
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
